import React from "react";
import { map, reduce, reduceRight } from "lodash";
import styled from "styled-components";
import { FontSize, FontWeight, Spacing, content } from "@glossy";
import { getFacetName } from "@linkfluence/i18n";
import { Facet } from "@linkfluence/models/facet";
import { getAllGenders, getGenderColor } from "@linkfluence/models/gender";
import { Locale } from "@linkfluence/models/locale";
import { Tone, getToneColor } from "@linkfluence/models/tone";
import Button from "@ui-kit/button";
import ColorPicker from "@ui-kit/color-picker";
import { tr } from "@radarly/i18n";

type PresetsGroup = {
  title: string;
  colors: { name: string; hex: string }[];
};

export interface IProps {
  currentColor: string;
  currentlyUsedColors: string[];
  queries: { name: string; hex: string }[];
  platforms: { name: string; hex: string }[];
  locale: Locale;
  onClickBack: () => void;
  onCancel: () => void;
  onValidate: (color: string) => void;
  onChange: (color: string) => void;
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  flex: 0;
  display: flex;
  align-items: center;
  padding: ${Spacing.Large} ${Spacing.ExtraLarge};
  border-bottom: 1px solid ${(props) => content(props, "tertiary")};
`;

const Title = styled.span`
  margin-left: ${Spacing.ExtraSmall};
  color: ${content};
  font-size: ${FontSize.Large};
  font-weight: ${FontWeight.Normal};
`;

const Body = styled.div`
  padding: ${Spacing.ExtraLarge};
  overflow: auto;
`;

const Footer = styled.div`
  border-top: 1px solid ${(props) => content(props, "tertiary")};
  flex: 0 0 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: ${Spacing.Medium};
`;

const PanelsCardEditionColorSelector = ({
  currentColor,
  currentlyUsedColors,
  queries,
  platforms,
  locale,
  onClickBack,
  onCancel,
  onValidate,
  onChange,
}: IProps) => {
  const facetColors: PresetsGroup[] = [
    { title: getFacetName(locale, Facet.Focuses), colors: queries },
    {
      title: getFacetName(locale, Facet.Platforms),
      colors: platforms,
    },
    {
      title: getFacetName(locale, Facet.Genders),
      colors: map(getAllGenders(true), (gender) => ({
        name: getFacetName(locale, Facet.Genders, gender),
        hex: getGenderColor(gender),
      })),
    },
    {
      title: getFacetName(locale, Facet.Tones),
      colors: map(Tone, (tone) => ({
        name: getFacetName(locale, Facet.Tones, tone),
        hex: getToneColor(tone),
      })),
    },
  ];

  const flattenFacetColors = reduceRight(
    facetColors,
    (flattenGroups, currentGroup) => ({
      ...flattenGroups,
      ...reduce(
        currentGroup.colors,
        (colorObject, currentColor) => ({
          ...colorObject,
          [currentColor.hex]: currentColor.name,
        }),
        {}
      ),
    }),
    {}
  );

  const presets = [
    currentlyUsedColors.length > 0 && {
      title: tr(
        locale,
        "CUSTOM_PAGES::EDITOR::COLOR_SELECTOR::CURRENTLY_USED_COLORS"
      ),
      colors: map(currentlyUsedColors, (color) => ({
        name: flattenFacetColors[color]
          ? `${String(flattenFacetColors[color])} (${color})`
          : color,
        hex: color,
      })),
    },
    ...facetColors,
  ];

  return (
    <Root>
      <Header>
        <Button icon="arrow-left" variant="tertiary" onClick={onClickBack} />
        <Title>
          {tr(locale, "CUSTOM_PAGES::EDITOR::COLOR_SELECTOR::TITLE")}
        </Title>
      </Header>
      <Body>
        <ColorPicker
          locale={locale}
          currentColor={currentColor}
          presets={presets}
          onChange={onChange}
        />
      </Body>
      <Footer>
        <Button
          size="s"
          variant="tertiary"
          text={tr(locale, "FORM::CANCEL")}
          onClick={onCancel}
        />
        <Button size="s" text={tr(locale, "FORM::OK")} onClick={onValidate} />
      </Footer>
    </Root>
  );
};

export default PanelsCardEditionColorSelector;
